import { isPresent } from '@ember/utils';
import { resolve } from 'rsvp';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import Enum from '@square/dashboard-utilities/utils/enum';
import Walkthrough from '@square/onboarding/models/walkthrough';
import WalkthroughStep from '@square/onboarding/models/walkthrough-step';
import AppointmentsWalkthroughPopover from '@square/onboarding/components/appointments/walkthrough-popover';

/**

## Walkthrough Service

The walkthrough service is used to manage walkthroughs. The walkthrough service
takes a Walkthrough object which contains configuration for each walkthrough step.
Walkthrough steps are rendered as popovers and are handled by the walkthrough-popover-manager
component.

Users go through the walkthrough by clicking through the popovers or performing
actions that trigger the current step to complete. To trigger a step to complete,
call `this.get('walkthrough').completeStep(stepName)`. If completeStep is called
and the user is not at that step in the walkthrough, the walkthrough will not go
to the next step.

For more information on how to create walkthroughs in Dashboard, check out the
[Walkthrough Popover Guide](/development/walkthrough.html).

### Usage

```js
// See models/walkthrough.js for full documentation of step options.
const walkthrough = Walkthrough.create({
  steps: [
    new WalkthroughStep({
      name: TourSteps.APP_DRAWER,
      targetElement: '.app-drawer',
      text: 'This is the app drawer!',
      buttonText: 'Next',
      placement: 'bottom-start'
      pointerEnabled: true,
    }),
    ...
  ]
});
this.get('currentWalkthrough').start(walkthrough);
```

*/

export const Walkthroughs = new Enum('AppointmentsCalendarWalkthrough', 'AppointmentsAddBusinessHoursDismissTooltip', 'AppointmentsCreateAiServicesWebInSituationCompletionTooltip', 'AppointmentsCreateServicesWebInSituationCompletionTooltip', 'AppointmentsCreateServicesWebDismissTooltip', 'AppointmentsSetUpOnlineBookingDismissTooltip', 'AppointmentsReviewSettingsDismissTooltip', 'AppointmentsAddStaffDismissTooltip', 'AppointmentsCreateClassOrAppointmentDismissTooltip', 'AppointmentsAddStaffDismissTooltip', 'AppointmentsImportCustomerListTooltip', 'AppointmentsViewedLastPageOfLocationSetupModal');
// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  screen: service(),
  i18n: service(),
  currentWalkthrough: null,
  currentStep: readOnly('currentWalkthrough.currentStep'),
  popoverIsActive: readOnly('currentWalkthrough.popoverIsActive'),
  start(walkthrough) {
    // Stop the current walkthrough if it is completed
    if (this.currentWalkthrough) {
      if (this.currentWalkthrough.isComplete) {
        this.stop();
      }
    }

    // User initiated walkthroughs take precedence.
    // They can be started even if another walkthrough is in progress.
    if (walkthrough.isUserInitiated) {
      this.set('currentWalkthrough', walkthrough);
      return this.currentWalkthrough.start();
    }

    // Don't start a new walkthrough if one is in progress.
    if (this.currentWalkthrough) {
      return null;
    }
    this.set('currentWalkthrough', walkthrough);
    return this.currentWalkthrough.start();
  },
  stop() {
    this.set('currentWalkthrough', null);
  },
  getWalkthrough(walkthroughName, onClose = () => {}, onComplete = () => {}) {
    if (this.walkthroughConfigs[walkthroughName]) {
      return Walkthrough.create({
        ...this.walkthroughConfigs[walkthroughName],
        onClose,
        onComplete
      });
    }
    return null;
  },
  // NB(judychang): in some pages (such as the Employee List page), components
  // render after the walkthrough popovers are positioned. This causes the
  // tooltips to be misaligned. So here we add a reposition() helper
  // that can be called to reposition the tooltip if needed.
  // TODO(judychang, RST-5631): we should refactor walkthrough#watchElements
  // to observe the targetElement's coordinates and update accordingly instead of
  // manually calling reposition() everywhere
  reposition() {
    const popper = this.get('currentWalkthrough.popper'); // eslint-disable-line ember/no-get
    if (isPresent(popper)) {
      popper.scheduleUpdate();
    }
  },
  completeStep(name) {
    if (this.currentWalkthrough) {
      return this.currentWalkthrough.completeStep(name);
    } else {
      return resolve();
    }
  },
  get walkthroughConfigs() {
    return {
      [Walkthroughs.AppointmentsCalendarWalkthrough]: {
        name: 'APPOINTMENTS_CALENDAR_WALKTHROUGH',
        steps: [new WalkthroughStep({
          name: 'CREATE_APPOINTMENT_BUTTON',
          targetElement: '[data-testid="create-appointment-button"]',
          text: this.i18n.t('appointments.calendar.walkthrough.title'),
          subtext: this.i18n.t('appointments.calendar.walkthrough.createAppointmentButtonStep'),
          placement: 'bottom-end',
          emphasizeClose: true,
          buttonText: this.i18n.t('button.next'),
          popoverComponent: AppointmentsWalkthroughPopover
        }), new WalkthroughStep({
          name: 'CREATE_APPOINTMENT_CALENDAR',
          targetElement: '.fc-header-toolbar',
          offset: '0px,400px',
          text: this.i18n.t('appointments.calendar.walkthrough.title'),
          subtext: this.i18n.t('appointments.calendar.walkthrough.createAppointmentCalendarStep'),
          placement: 'bottom-center',
          emphasizeClose: true,
          buttonText: this.i18n.t('common.gotIt'),
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true
      },
      [Walkthroughs.AppointmentsAddBusinessHoursDismissTooltip]: {
        name: 'ADD_BUSINESS_HOURS_DISMISS_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'ADD_BUSINESS_HOURS_DISMISS_TOOLTIP',
          targetElement: '#homepage-link',
          text: this.i18n.t('appointments.homepage.tooltip.addBusinessHours.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.addBusinessHours.description'),
          placement: 'bottom-end',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true,
        popoverClassNames: 'appointments-business-hours-dismiss-tooltip'
      },
      [Walkthroughs.AppointmentsCreateAiServicesWebInSituationCompletionTooltip]: {
        name: 'CREATE_AI_SERVICES_WEB_IN_SITUATION_COMPLETION_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'CREATE_AI_SERVICES_WEB_IN_SITUATION_COMPLETION_TOOLTIP',
          targetElement: '#library-dropdown',
          text: this.i18n.t('appointments.homepage.tooltip.createServicesWeb.inSituation.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.createServicesWeb.inSituation.description2'),
          placement: 'right',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true
      },
      [Walkthroughs.AppointmentsCreateServicesWebInSituationCompletionTooltip]: {
        name: 'CREATE_SERVICES_WEB_IN_SITUATION_COMPLETION_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'CREATE_SERVICES_WEB_IN_SITUATION_COMPLETION_TOOLTIP',
          targetElement: '#library-dropdown',
          text: this.i18n.t('appointments.homepage.tooltip.createServicesWeb.inSituation.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.createServicesWeb.inSituation.description'),
          placement: 'right',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true
      },
      [Walkthroughs.AppointmentsCreateServicesWebDismissTooltip]: {
        name: 'CREATE_SERVICES_WEB_DISMISS_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'CREATE_SERVICES_WEB_DISMISS_TOOLTIP',
          targetElement: '#services-link',
          text: this.i18n.t('appointments.homepage.tooltip.createServicesWeb.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.createServicesWeb.description'),
          placement: 'right',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true
      },
      [Walkthroughs.AppointmentsSetUpOnlineBookingDismissTooltip]: {
        name: 'SET_UP_APPOINTMENTS_ONLINE_BOOKING_DISMISS_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'SET_UP_APPOINTMENTS_ONLINE_BOOKING_DISMISS_TOOLTIP',
          targetElement: '#online-booking-dropdown',
          text: this.i18n.t('appointments.homepage.tooltip.setUpAppointmentsOnlineBooking.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.setUpAppointmentsOnlineBooking.description'),
          placement: 'right',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true
      },
      [Walkthroughs.AppointmentsReviewSettingsDismissTooltip]: {
        name: 'REVIEW_APPOINTMENTS_SETTINGS_DISMISS_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'REVIEW_APPOINTMENTS_SETTINGS_DISMISS_TOOLTIP',
          targetElement: '#settings-dropdown',
          text: this.i18n.t('appointments.homepage.tooltip.reviewAppointmentSettings.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.reviewAppointmentSettings.description'),
          placement: 'right',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true
      },
      [Walkthroughs.AppointmentsAddStaffDismissTooltip]: {
        name: 'ADD_APPOINTMENTS_STAFF_DISMISS_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'ADD_APPOINTMENTS_STAFF_DISMISS_TOOLTIP',
          targetElement: '#staff-settings-link',
          text: this.i18n.t('appointments.homepage.tooltip.addStaff.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.addStaff.description'),
          placement: 'right',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true
      },
      [Walkthroughs.AppointmentsCreateClassOrAppointmentDismissTooltip]: {
        name: 'CREATE_APPOINTMENTS_CLASS_OR_APPOINTMENT_DISMISS_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'CREATE_APPOINTMENTS_CLASS_OR_APPOINTMENT_DISMISS_TOOLTIP',
          targetElement: '#calendar-link',
          text: this.i18n.t('appointments.homepage.tooltip.createClassOrAppointment.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.createClassOrAppointment.description'),
          placement: 'right',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true
      },
      [Walkthroughs.AppointmentsImportCustomerListTooltip]: {
        name: 'IMPORT_APPOINTMENTS_CUSTOMER_LIST_TOOLTIP',
        steps: [new WalkthroughStep({
          name: 'IMPORT_APPOINTMENTS_CUSTOMER_LIST_TOOLTIP',
          targetElement: '#homepage-link',
          text: this.i18n.t('appointments.homepage.tooltip.importCustomerList.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.importCustomerList.description'),
          placement: 'bottom-start',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true,
        popoverClassNames: 'dashboard-nav-hamburger-tooltip'
      },
      [Walkthroughs.AppointmentsViewedLastPageOfLocationSetupModal]: {
        name: 'VIEWED_LAST_PAGE_OF_LOCATION_SETUP_MODAL',
        steps: [new WalkthroughStep({
          name: 'VIEWED_LAST_PAGE_OF_LOCATION_SETUP_MODAL',
          targetElement: '#homepage-link',
          text: this.i18n.t('appointments.homepage.tooltip.viewedLastPageOfLocationSetupModal.title'),
          subtext: this.i18n.t('appointments.homepage.tooltip.viewedLastPageOfLocationSetupModal.description'),
          placement: 'bottom-start',
          emphasizeClose: false,
          showProgressText: false,
          popoverComponent: AppointmentsWalkthroughPopover
        })],
        shouldShowCloseButton: true,
        popoverClassNames: 'dashboard-nav-hamburger-tooltip'
      }
    };
  }
});